import { forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';

import { OrganisationArea } from '../organisation-area';

import { EntityTable, IEntityTable } from './entity-table/EntityTable';
import { useEntityDataTable } from './entity-table/use-entity-data-table';
import { useFileActions } from './FileActions';
import schema from './schemata/customer-table-schema.json';

type ICustomerTableCustomColumns = Record<string, never>;

const defaultSort = [{ field: 'name', direction: SortDirection.ASCENDING }];

export const CustomersTable = forwardRef<TableInstance, IEntityTable>(
  function CustomersTable(props, ref) {
    const { entity: entityType } = schema as never;

    const { prepareRowDisplayModifiers, ...dataTable } = useEntityDataTable<
      Customer,
      ICustomerTableCustomColumns
    >({
      schema: schema as never,
      defaultSort,
    });

    const rowModifiers = useCallback(
      (value) => {
        const modifiers = prepareRowDisplayModifiers(value);
        return { ...modifiers, isFaded: value.extinct === -1 };
      },
      [prepareRowDisplayModifiers]
    );

    const { fileActionDefinitions, openInNewTab } = useFileActions({
      entity: entityType,
    });

    const { t } = useTranslation();

    return (
      <EntityTable
        ref={ref}
        areas={{
          left: {
            title: t('COMMON.ORGANISATION'),
            content: <OrganisationArea entity={entityType} />,
            resizable: true,
            collapsible: true,
            custom: true,
          },
        }}
        actions={{
          custom: {
            left: fileActionDefinitions,
          },
          openInNewTab,
          createWidget: false,
        }}
        prepareRowDisplayModifiers={rowModifiers}
        {...dataTable}
        {...props}
      />
    );
  }
);
